import { useState, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  HStack,
  Text,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  useDisclosure,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { Heart } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { useSelector } from 'react-redux';
import { selectIsLoggedIn } from '@/store/slices/authSlice';
import { ReactionPicker } from './ReactionPicker';
import { ReactionDisplay } from './ReactionDisplay';
import { REACTION_EMOJIS, type ReactionType, type ReactionCounts, type ReactedByUser } from '@/types/social';

const popAnimation = keyframes`
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); opacity: 1; }
`;

interface ReactionBarProps {
  totalCount: number;
  counts: ReactionCounts;
  userReaction: ReactionType | null;
  recentUsers: ReactedByUser[];
  onReact: (reaction: ReactionType) => Promise<void>;
  onRemoveReaction: () => Promise<void>;
  onShowReactions?: () => void;
  isLoading?: boolean;
}

export const ReactionBar = ({
  totalCount,
  counts,
  userReaction,
  recentUsers,
  onReact,
  onRemoveReaction,
  onShowReactions,
  isLoading = false,
}: ReactionBarProps) => {
  const navigate = useNavigate();
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [showAnimation, setShowAnimation] = useState(false);
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const isLongPress = useRef(false);

  const handleSelect = useCallback(
    async (reaction: ReactionType) => {
      onClose();

      // If user clicks same reaction, remove it
      if (userReaction === reaction) {
        await onRemoveReaction();
        return;
      }

      // Show animation
      setShowAnimation(true);
      setTimeout(() => setShowAnimation(false), 500);

      await onReact(reaction);
    },
    [userReaction, onReact, onRemoveReaction, onClose]
  );

  const handleMouseDown = useCallback(() => {
    if (!isLoggedIn) return;

    isLongPress.current = false;
    longPressTimer.current = setTimeout(() => {
      isLongPress.current = true;
      onOpen();
    }, 500); // Long press threshold
  }, [isLoggedIn, onOpen]);

  const handleMouseUp = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();

      // Require authentication
      if (!isLoggedIn) {
        navigate({ to: '/login' });
        return;
      }

      // If it was a long press, don't do anything (picker is open)
      if (isLongPress.current) {
        isLongPress.current = false;
        return;
      }

      // Quick tap: toggle love reaction or remove current
      if (userReaction) {
        await onRemoveReaction();
      } else {
        setShowAnimation(true);
        setTimeout(() => setShowAnimation(false), 500);
        await onReact('love');
      }
    },
    [isLoggedIn, navigate, userReaction, onReact, onRemoveReaction]
  );

  // Get button appearance based on reaction
  const getButtonContent = () => {
    if (userReaction) {
      return (
        <HStack spacing={1}>
          <Text fontSize="lg">{REACTION_EMOJIS[userReaction]}</Text>
          <Text display={{ base: 'none', lg: 'inline' }}>
            {totalCount > 0 ? `(${totalCount})` : ''}
          </Text>
        </HStack>
      );
    }

    return (
      <HStack spacing={1.5}>
        <Heart size={18} />
        <Text display={{ base: 'none', lg: 'inline' }}>
          {totalCount > 0 ? `React (${totalCount})` : 'React'}
        </Text>
        <Text display={{ base: 'inline', lg: 'none' }}>
          {totalCount > 0 ? `(${totalCount})` : ''}
        </Text>
      </HStack>
    );
  };

  return (
    <Box position="relative">
      {/* Reaction Display (Social Proof) */}
      {totalCount > 0 && (
        <Box mb={2}>
          <ReactionDisplay
            totalCount={totalCount}
            counts={counts}
            recentUsers={recentUsers}
            userReaction={userReaction}
            onClick={onShowReactions}
          />
        </Box>
      )}

      {/* Reaction Button with Popover Picker */}
      <Popover
        isOpen={isOpen}
        onClose={onClose}
        placement="top"
        closeOnBlur={true}
        autoFocus={false}
      >
        <PopoverTrigger>
          <Button
            variant="ghost"
            color={userReaction ? 'var(--color-purple-600)' : 'var(--card-text-2)'}
            _hover={{ bg: userReaction ? 'var(--color-purple-50)' : 'var(--card-surface-2)' }}
            size={{ base: 'xs', lg: 'sm' }}
            flex={1}
            isDisabled={isLoading}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleMouseDown}
            onTouchEnd={handleMouseUp}
            onClick={handleClick}
            fontSize={{ base: 'xs', lg: 'sm' }}
            position="relative"
            overflow="visible"
          >
            {getButtonContent()}

            {/* Animated Emoji on React */}
            {showAnimation && userReaction && (
              <Box
                position="absolute"
                top="-10px"
                left="50%"
                transform="translateX(-50%)"
                fontSize="display"
                animation={`${popAnimation} 0.5s ease-out forwards`}
                pointerEvents="none"
              >
                {REACTION_EMOJIS[userReaction]}
              </Box>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          bg="transparent"
          border="none"
          boxShadow="none"
          w="auto"
          _focus={{ boxShadow: 'none' }}
        >
          <PopoverBody p={0}>
            <ReactionPicker
              onSelect={handleSelect}
              currentReaction={userReaction}
              isLoading={isLoading}
            />
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};
